import { css } from '@styled-system/css';
import { Link as LinkIcon } from 'lucide-react';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';

import type { VestiaireSearchForm } from '@/features/productSearch/vestiaire/useVestiaireSearchForm';
import { VESTIAIRE_SEARCH_BASE_URL } from '@/features/productSearch/vestiaire/vestiaireUrl';
import { ignoredPartsText } from '@/features/productSearch/vestiaire/vestiaireUrlParts';
import { Accordion } from '@/shared/components/ui/Accordion';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { useUserLanguage } from '@/shared/hooks/useUserLanguage';

// ============================================================================
// Import d'une URL de recherche Vestiaire Collective — même gabarit que l'import Vinted de
// SearchModal (accordéon `urlImport`, création seulement). État et lecture serveur :
// useVestiaireUrlImport (tenu par useVestiaireSearchForm, pour l'import implicite à l'envoi).
// ============================================================================

const column = css({ display: 'flex', flexDirection: 'column', gap: '3' });
const hint = css({ fontSize: 'caption', color: 'text.tertiary' });
const catalogLink = css({
  color: 'brand.primary',
  textDecoration: 'underline',
  _hover: { color: 'brand.primaryHover' },
});
const inlineGap = css({ display: 'flex', gap: '3', alignItems: 'flex-end' });
const errorText = css({ fontSize: 'caption', color: 'error.default' });

export interface VestiaireUrlImportProps {
  search: VestiaireSearchForm;
}

export const VestiaireUrlImport: FC<VestiaireUrlImportProps> = ({ search }) => {
  const { t } = useTranslation();
  const { language } = useUserLanguage();
  const { url, setUrl, error, ignored, importUrl } = search.urlImport;

  return (
    <Accordion.Item value="urlImport">
      <Accordion.Header icon={<LinkIcon size={16} strokeWidth={1.75} />} label={t('vestiaireSearch.urlImportLabel')} />
      <Accordion.Content>
        <div className={column}>
          <span className={hint}>
            {t('vestiaireSearch.urlImportHint')}{' '}
            <a href={VESTIAIRE_SEARCH_BASE_URL[language]} target="_blank" rel="noopener noreferrer" className={catalogLink}>
              {t('vestiaireSearch.openCatalog')}
            </a>
          </span>
          <div className={inlineGap}>
            <Input
              placeholder={`${VESTIAIRE_SEARCH_BASE_URL[language]}#brand=…`}
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              leading={<LinkIcon size={14} strokeWidth={1.75} />}
              state={error ? 'error' : 'default'}
            />
            {/* Arbre requis pour reconstruire les chemins de catégories : bouton inactif sans lui. */}
            <Button
              variant="secondary"
              size="md"
              onClick={async () => {
                await importUrl();
              }}
              disabled={!url.trim() || !search.treeReady}
              showLoader
            >
              {t('searchModal.import')}
            </Button>
          </div>
          {error && <span className={errorText}>{error}</span>}
          {ignored.length > 0 && (
            <span className={hint}>
              {t('vestiaireSearch.urlImportIgnored', { parts: ignoredPartsText(ignored, t) })}
            </span>
          )}
        </div>
      </Accordion.Content>
    </Accordion.Item>
  );
};
